import React, { useEffect, useMemo, useRef } from "react";
import {
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  Selection,
  IColumn,
  CheckboxVisibility,
} from "@fluentui/react/lib/DetailsList";
import { Stack } from "@fluentui/react";
import ShimmerList from "@/ui-controls/ShimmerList";
import { PaginationFooter } from "@/ui-controls/PaginationFooter";

export interface ServerDataGridColumn extends IColumn {}

const customStyles = {
  root: {
    selectors: {
      ".ms-DetailsRow": {
        minHeight: 45,
        height: 45,
      },
    },
  },
};

interface Props {
  items: any[];
  columns: ServerDataGridColumn[];
  pageSize?: number;
  currentPage?: number;
  totalPages?: number;
  totalCount?: number;
  onPageSizeChange?: (pageSize: number) => void;
  onPageChange?: (page: number) => void;
  loading?: boolean;
  selectedCount?: number;
  onSelectedKeysChange?: (keys: Array<string | number>) => void;
}

export const ServerDataGrid = ({
  items,
  columns,
  pageSize = 10,
  currentPage = 1,
  totalCount = 0,
  onPageSizeChange,
  onPageChange,
  loading = false,
  selectedCount = 0,
  onSelectedKeysChange,
}: Props) => {
  const itemsWithKeys = useMemo(
    () =>
      items.map((item) => {
        const key = item.id ?? item.Id ?? item.key ?? Math.random().toString();
        return { ...item, key };
      }),
    [items]
  );

  const selectionRef = useRef<Selection>();
  if (!selectionRef.current)
  {
    selectionRef.current = new Selection({
      selectionMode: SelectionMode.multiple,
      canSelectItem: (item) => item?.Id !== -1 && item?.id !== -1,
      onSelectionChanged: () => {
        if (onSelectedKeysChange && selectionRef.current) {
          const keys = selectionRef.current.getSelection().map((row) => row.key);
          onSelectedKeysChange(keys);
        }
      },
    });
  }

  const selection = selectionRef.current;

  useEffect(() => {
    selection.setChangeEvents(false);
    selection.setItems(itemsWithKeys, false);
    selection.setChangeEvents(true);
  }, [itemsWithKeys, selection]);

  return (
    <Stack
      styles={{
        root: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          minWidth: 0,
        },
      }}
    >
      <Stack.Item grow styles={{ root: { overflow: "auto", minHeight: 0 } }}>
        {loading ? (
          <ShimmerList numberOfColumns={columns.length} />
        ) : (
          <div style={{ width: "100%", overflowX: "auto" }}>
            <DetailsList
              items={itemsWithKeys}
              compact={false}
              columns={columns}
              selection={selection}
              selectionMode={SelectionMode.multiple}
              layoutMode={DetailsListLayoutMode.justified}
              checkboxVisibility={CheckboxVisibility.always}
              isHeaderVisible={true}
              styles={customStyles}
            />
          </div>
        )}
      </Stack.Item>
      <Stack.Item
        styles={{
          root: {
            marginTop: "auto",
            borderTop: "1px solid #edebe9",
            paddingTop: 16,
            backgroundColor: "#fff",
            position: "sticky",
            bottom: 0,
            zIndex: 1,
          },
        }}
      >
        <PaginationFooter
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={onPageChange ?? (() => {})}
          onPageSizeChange={onPageSizeChange}
          selectedCount={selectedCount}
        />
      </Stack.Item>
    </Stack>
  );
};
